import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addToCheckoutCart, checkoutSetRoomsConnected, initBedbankSession, initialiseCheckout, removeItem, setCheckoutGuestMode, validateOfferDiscount } from 'actions/CheckoutActions'
import { createGhaVoucher } from 'actions/MetasearchVoucherActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { FlightViewTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { generateBedbankAccommodationCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { addToUserCart } from 'actions/UserCartActions'
import { getSource, getSourceForProperty, ghaPricedRequest } from 'selectors/offerSelectors'
import { getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { fetchAccumulatedRatesForBedbankOffer, updateBedbankPricingData } from 'actions/BedbankOfferActions'
import invariant from 'tiny-invariant'
import { sum, uniqueBy } from 'lib/array/arrayUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import type { AppDispatch } from 'src/client/store'
import { generateInsuranceItem } from 'lib/checkout/insurance/cart'
import { INSURANCE_PRODUCT_ID } from 'constants/insurance'
import useMetasearchAccountType from 'hooks/useMetasearchAccountType'
import useMetasearchAdvertisedPrice from 'hooks/useMetasearchAdvertisedPrice'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import useMetasearchRuleId from 'hooks/useMetasearchRuleId'
import useGuestCheckoutEligibility from 'hooks/useGuestCheckoutEligibility'
import { setPendingGhaVoucher } from 'lib/metasearch/pendingGhaVoucher'

type SessionResponse = Awaited<ReturnType<typeof initBedbankSession>>

function isValidSession(session: SessionResponse): session is Extract<SessionResponse, { sessionId: string }> {
  return !!('sessionId' in session)
}

interface ChangedRate {
  rate: App.BedbankRate
  newPrice?: number
  newLuxPlusPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

function calculatePriceChange(changedRates: Array<ChangedRate>, isLuxPlusBooking: boolean): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = sum(changedRates, ch => {
    if (isLuxPlusBooking && ch.rate.totals.memberInclusive) {
      return ch.rate.totals.memberInclusive
    }
    return ch.rate.totals.inclusive
  })
  const newDisplayPrice = sum(changedRates, ch => {
    const newLuxPlusPrice = ch.newLuxPlusPrice ?? ch.rate.totals.memberInclusive ?? 0
    if (isLuxPlusBooking && newLuxPlusPrice) {
      return newLuxPlusPrice
    }
    return ch.newPrice ?? ch.rate.totals.inclusive
  })
  const oldPropertyFees = sum(changedRates, ch => ch.rate.totals.propertyFees ?? 0)
  const newPropertyFees = sum(changedRates, ch => ch.newPropertyFees ?? ch.rate.totals.propertyFees ?? 0)
  return { oldDisplayPrice, newDisplayPrice, oldPropertyFees, newPropertyFees }
}

/**
 * Dispatches an action to patch changed rates in the offer store and returns semi-patched rates
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRates(dispatch: AppDispatch, { offerId, checkIn, checkOut, changedRates }: {
  offerId: string
  checkIn: string
  checkOut: string
  changedRates: Array<ChangedRate>
}): Array<App.BedbankRate> {
  return changedRates.map(({
    rate,
    newPrice,
    newLuxPlusPrice,
    newPropertyFees,
    rooms,
  }) => {
    const resolvedPrice = newPrice ?? rate.totals.inclusive
    const resolvedLuxPlusPrice = newLuxPlusPrice ?? rate.totals.memberInclusive ?? 0
    const resolvedPropertyFees = newPropertyFees ?? rate.totals.propertyFees ?? 0

    if (
      resolvedPrice === rate.totals.inclusive &&
      resolvedLuxPlusPrice === (rate.totals.memberInclusive ?? 0) &&
      resolvedPropertyFees === (rate.totals.propertyFees ?? 0)
    ) {
      // Not all rates have necessarily changed.
      return rate
    }

    dispatch(updateBedbankPricingData(
      offerId,
      rate.roomId,
      rate.id,
      rate.isFlightBundle,
      checkIn,
      checkOut,
      rooms,
      // NB: The price change error only tells us the total, not the room-level changes. To work
      // around this, we estimate by dividing by the number of rooms.
      {
        data: [{
          inclusive: resolvedPrice / rooms.length,
          memberInclusive: resolvedLuxPlusPrice / rooms.length,
          propertyFees: resolvedPropertyFees / rooms.length,
        }],
      },
    ))

    // We don't need to be as thorough here; we only need to patch the fields that're used to
    // perform a bedbank service enquiry request.
    const newRate: App.BedbankRate = {
      ...rate,
      totals: {
        ...rate.totals,
        inclusive: resolvedPrice,
        memberInclusive: resolvedLuxPlusPrice,
        propertyFees: resolvedPropertyFees,
      },
    }

    return newRate
  })
}

function refreshSingleRoomRates({ dispatch, offerId, rooms, checkInDate, checkOutDate }: {
  dispatch: AppDispatch
  offerId: string
  rooms: Array<App.Occupants>
  checkInDate: string
  checkOutDate: string
}) {
  dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, rooms, checkInDate, checkOutDate, true))
}

function refreshMultiRoomRates({ dispatch, offerId, roomsWithData, checkInDate, checkOutDate }: {
  dispatch: AppDispatch
  offerId: string
  roomsWithData: Array<RoomWithData>
  checkInDate: string
  checkOutDate: string
}) {
  const uniqueRooms = uniqueBy(roomsWithData.map(({ room }) => room), room => serialiseOccupancy([room]))
  uniqueRooms.forEach(room => {
    dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, [room], checkInDate, checkOutDate, true))
  })
}

/**
 * Selector values read at call time. The hook's own `useAppSelector` values are stale inside the
 * click that set the GHA-404 public-rate marker, and the retry after the price modal must enquire
 * and mint with the public source.
 */
function readLiveGhaPricing(dispatch: AppDispatch, offerId: string): {
  source: string
  sourceForProperty: string
  ghaPriceMatch: ReturnType<typeof getGhaPriceMatchForProperty>
} {
  return dispatch((_dispatch, getState) => {
    const state = getState()
    return {
      source: getSource(state),
      sourceForProperty: getSourceForProperty(state, offerId),
      ghaPriceMatch: getGhaPriceMatchForProperty(state, offerId),
    }
  })
}

/**
 * GHA-404: the same rate priced at the public source, once the marker has switched the selectors.
 * Rate caches do not key on source, so this is a forced refetch; the page re-renders with public
 * rates whether or not the shopper accepts.
 */
function fetchPublicBedbankRate(dispatch: AppDispatch, { offerId, rate, rooms, checkInDate, checkOutDate }: {
  offerId: string
  rate: App.BedbankRate
  rooms: Array<App.Occupants>
  checkInDate: string
  checkOutDate: string
}): Promise<App.BedbankRate | undefined> {
  return new Promise((resolve) => {
    dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, rooms, checkInDate, checkOutDate, true, (result) => {
      if (result.status !== 'success') {
        resolve(undefined)
        return
      }
      resolve(
        result.data.find(candidate => candidate.id === rate.id) ??
        result.data.find(candidate => candidate.roomId === rate.roomId && candidate.groupId === rate.groupId),
      )
    }))
  })
}

interface SingleRoomArgs {
  offer: App.BedbankOffer
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<OfferPageRoom>
  originAirport?: App.Airport
  isLoggedIn?: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  rateListId: string
  roomPosition: number
  roomListId: string
  flightPrice?: number
  connectRooms: boolean
}

interface SingleRoomBookNowArgs extends SingleRoomArgs {
  shouldApplyDiscount: boolean
  /** The GHA discount the tile took off this rate, for the whole stay. */
  ghaDiscountAmount?: number
  checkoutSource?: App.CheckoutSource
}

export function useBedbankSingleRoomBookNowCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  rateListId,
  roomPosition,
  roomListId,
  flightPrice,
  connectRooms,
  shouldApplyDiscount,
  ghaDiscountAmount = 0,
  checkoutSource,
}: SingleRoomBookNowArgs) {
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()
  const metasearchPriceId = useMetasearchPriceId()
  const accountType = useMetasearchAccountType()
  const {
    checkoutSources: experiencesPassportCheckoutSources,
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()
  const metasearchRuleId = useMetasearchRuleId()
  const metasearchAdvertised = useMetasearchAdvertisedPrice()
  const { canProceedAsGuest, activate: activateGuestCheckoutExperiment } = useGuestCheckoutEligibility(isLoggedIn)

  const showModal = useContext(ModalContext)

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)
  const onBook = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    keepBrowsing = false,
    isInsuranceSelected?: boolean,
    discountValue?: number,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      if (experiencesPassportFetching) {
        showSnackbar(intl.formatMessage({
          id: 'UCznyyUL7pToKg3_BrQl',
          defaultMessage: 'Please wait...',
          description: 'Processing modal message while bedbank booking is being processed',
        }), 'neutral')
        return
      }

      trackOfferPageEvent('BOOK_NOW_PRESSED')
      let isGuestCheckout = false
      // Read from the store, not the `isLoggedIn` prop: the prop is stale once the sign-in modal
      // below resolves (no re-render in between), and a GHA-404 retry re-enters this callback after
      // the shopper may have signed in.
      const isLoggedInNow = dispatch((_dispatch, getState) => selectLoggedIn(getState()))
      let hasAuthenticatedUser = isLoggedInNow
      if (!isLoggedInNow && !offer.walledGarden) {
        const guestCheckoutPurchase = {
          isWalledGarden: !!offer.walledGarden,
          isMultiRoom: false,
          itemTypes: [
            ...rooms.map(() => 'bedbankHotel' as const),
            ...(withFlights && originAirport ? ['flight' as const] : []),
            ...(isInsuranceSelected ? ['insurance' as const] : []),
            ...(experiencesPassportCheckoutSources.length ? ['experience' as const] : []),
          ],
        }
        const isGuestCheckoutEligible = canProceedAsGuest(guestCheckoutPurchase)
        activateGuestCheckoutExperiment(guestCheckoutPurchase)
        if (isGuestCheckoutEligible) {
          // Eligible GHA customers use guest checkout without seeing the sign-in modal.
          isGuestCheckout = true
        } else {
          const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
          hasAuthenticatedUser = result.loggedIn
          if (!result.loggedIn) {
            return
          }
        }
      }

      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal
          message={intl.formatMessage({
            id: 'UCznyyUL7pToKg3_BrQl',
            defaultMessage: 'Please wait...',
            description: 'Processing modal message while bedbank booking is being processed',
          })}
          fullHeight
        />
      })

      const pkg = packageView.package

      try {
        const { source, sourceForProperty, ghaPriceMatch } = readLiveGhaPricing(dispatch, offer.id)
        const priced = ghaPricedRequest(source, ghaPriceMatch)
        // Initiate a session
        const session = await initBedbankSession(dispatch, {
          propertyId: offer.id,
          roomTypeId: rate.roomId,
          roomRateId: rate.id,
          token: rate.token,
          rateGroupId: rate.groupId,
          bedGroupId: rate.bedGroups[0].id,
          isFlightBundle: rate.isFlightBundle,
          csBooking: !!isSpoofed,
          region: currentRegionCode,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          price: rate.totals.inclusive,
          propertyFees: rate.totals.propertyFees,
          memberInclusive: rate.totals.memberInclusive,
          subscriber,
          source: priced.source,
          gha: priced.gha,
        })
        if (discountValue) {
          dispatch(validateOfferDiscount({
            offerId: offer.id,
            region: currentRegionCode,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            occupancy: sumUpOccupancies(rooms).toString(),
            discount: discountValue.toString(),
            fkRoomTypeId: rate.roomId,
            roomRateId: rate.id,
          }))
        }

        closeProcessingModal()
        if (!isValidSession(session)) {
          const changedRates = [{
            rate,
            newPrice: session.price,
            newLuxPlusPrice: session.luxplusPrice,
            newPropertyFees: session.propertyFees,
            rooms,
          }]
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            onBook(newRates[0], ratePosition, withFlights, keepBrowsing)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        }

        if (isValidSession(session)) {
          const isGhaPriceMatchedRate = !!ghaPriceMatch && rate.calculationMethod === 'matched_gha'
          const accommodationItems = generateBedbankAccommodationCheckoutItems(
            offer,
            pkg,
            rate,
            {
              checkIn: checkInDate,
              checkOut: checkOutDate,
              duration: moment(checkOutDate).diff(moment(checkInDate), 'days'),
            },
            rooms,
            session.sessionId,
            undefined, // bundledItemIds
            ghaPriceMatch,
            isGhaPriceMatchedRate,
          )

          const flightItems: Array<App.Checkout.FlightItem> = []
          if (withFlights && originAirport) {
            const flightOccupants = countOccupantsForFlights(rooms)

            flightItems.push(generateFlightCheckoutItem({
              originAirportCode: originAirport?.code,
              destinationAirportCode: offer.flights.airportCode!,
              fareType: 'return',
              viewType: FlightViewTypes.TWO_ONE_WAYS,
              occupants: flightOccupants,
              passengers: generatePassengersFromOccupants(flightOccupants),
              quotedFare: flightPrice,
              bundledItemIds: accommodationItems.map(item => item.itemId),
            }))
          }

          const insuranceItems: Array<App.Checkout.InsuranceItem> = []
          if (isInsuranceSelected) {
            const bedbankRoomIds = accommodationItems.map(item => item.roomId)
            const insuranceItem = generateInsuranceItem({ product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' }, customCoverage: rate.totals.total, bedbankRoomIds, isAccommodationBundle: true })
            insuranceItems.push(insuranceItem)
            trackClientEvent({
              subject: 'flex_cfmr_package',
              action: 'add_to_cart',
              type: 'interaction',
              category: 'insurance',
            })
          }

          const checkoutItems = [
            ...accommodationItems,
            ...flightItems,
            ...insuranceItems,
          ]
          dispatch(addToCheckoutCart(checkoutItems, checkoutSource ? { checkoutSource } : undefined))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))

          fireEcommerceGTMEvent(addToCartGTM(offer, currency, session.price, sumUpOccupancies(rooms), 'hotel'))
          trackEvent(addToCart({
            type: 'bedbank',
            ratePosition,
            rateListId,
            roomPosition,
            roomListId,
            rate,
            isFlightBundle: !!withFlights,
            offer,
            occupancy: rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            // Same price basis as the GTM event above, so the two stay reconcilable.
            price: session.price,
            value: rate.value,
            currency,
            staysOnPage: keepBrowsing,
          }))

          if (keepBrowsing) {
            showSnackbar(intl.formatMessage({
              id: 'nY4_exwST6mDs_OWpjKs',
              defaultMessage: 'Your item has been added to the cart.',
              description: 'Snackbar message after adding a bedbank item to the cart',
            }), 'success')
            return
          }

          // GHA Discounting v1, only discounts the cheapest hotel-only rate of an offer page,
          // gated by parent-computed shouldApplyDiscount. The thunk additionally
          // gates on token/propertyId/dates/occupancy === ['1'] or ['2'].
          //
          // This mirrors the autoAppliedPromoCode pattern: the voucher id is set
          // in checkout state here (before navigateToCheckout) and consumed on the
          // checkout page. Keep this awaited and ordered after cart setup but before
          // navigation — CHECKOUT_INITIALISE resets checkout state via ...initialState,
          // so setting the voucher after an init would silently wipe it.
          //
          // A voucher is assigned to a customer as it is created, so with no account yet we
          // stash the inputs and checkout creates it after registration or login.
          // TODO(GHA-661): the rate svc-bedbank repriced to Google's price still mints so
          // svc-connection-google can flag the shopper (GHA-404). Once Hotel Sight has its own
          // endpoint, go back to `shouldApplyDiscount && !ghaPriceMatch`.
          if (ghaPriceMatch ? isGhaPriceMatchedRate : shouldApplyDiscount) {
            const voucherInput = {
              token: metasearchPriceId,
              accountType,
              propertyId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              rooms,
              mx: rate.mx,
              rateRuleId: metasearchRuleId,
              source: sourceForProperty,
              ...metasearchAdvertised,
            }
            if (hasAuthenticatedUser) {
              const outcome = await dispatch(createGhaVoucher(voucherInput))
              if (outcome === 'public_rate') {
                // GHA-404: the service flagged the shopper and withheld the voucher; the session is
                // now public-rate only. Show the public price if it differs, then book again: the
                // retry enquires with the public source and skips the mint. Never continue on this
                // session, which was enquired at the member rate.
                // Its booking is already in the cart: take it out, so the retry doesn't add a second
                // copy and Return to offer doesn't leave a booking the shopper refused.
                checkoutItems.forEach(item => dispatch(removeItem(item.itemId)))
                const publicRate = await fetchPublicBedbankRate(dispatch, { offerId: offer.id, rate, rooms, checkInDate, checkOutDate })
                const changedRates = publicRate ? [{
                  rate,
                  newPrice: publicRate.totals.inclusive,
                  // A public rate without a LuxPlus price charges members the standard price. Left
                  // undefined, the modal would show the member-rate LuxPlus price this session lost.
                  newLuxPlusPrice: publicRate.totals.memberInclusive ?? publicRate.totals.inclusive,
                  newPropertyFees: publicRate.totals.propertyFees,
                  rooms,
                }] : []
                const priceChange = calculatePriceChange(changedRates, !!subscriber?.tier)
                // The shopper saw the rate less the GHA discount, which the withheld voucher takes away.
                priceChange.oldDisplayPrice = Math.max(priceChange.oldDisplayPrice - ghaDiscountAmount, 0)
                const hasPriceChanged = !!publicRate && (
                  priceChange.oldDisplayPrice !== priceChange.newDisplayPrice ||
                  priceChange.oldPropertyFees !== priceChange.newPropertyFees
                )
                if (hasPriceChanged) {
                  const hasUserAcceptedPublicRate = await showModal<boolean | undefined>(<BedbankChangePriceModal {...priceChange} />)
                  trackClientEvent({
                    subject: 'gha_public_rate',
                    action: hasUserAcceptedPublicRate ? 'accept' : 'return_to_offer',
                    category: 'metasearch',
                    type: 'interaction',
                  })
                  if (!hasUserAcceptedPublicRate) return
                }
                // The refetched rate carries its own id, token and price from the public source. If
                // the refetch failed, the retry's enquiry comes back repriced and the existing
                // price-change check (`isValidSession`) shows the modal.
                onBook(publicRate ?? rate, ratePosition, withFlights, keepBrowsing, isInsuranceSelected, discountValue)
                return
              }
            } else {
              setPendingGhaVoucher(voucherInput)
            }
          }

          // Guest checkout follows the same ordering rule as the voucher above:
          // set after cart initialisation (which resets checkout state) and
          // before navigation so the checkout auth gate admits the guest.
          if (isGuestCheckout) {
            dispatch(setCheckoutGuestMode(true))
          }

          dispatch(navigateToCheckout())
        }
      } catch(_error) {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        showModal(<BedbankSoldOutModal />)
      }
    }
  }, [accountType, checkInDate, checkOutDate, checkoutSource, connectRooms, currency, currentRegionCode, dispatch, experiencesPassportCheckoutSources, experiencesPassportFetching, flightPrice, ghaDiscountAmount, intl, isSpoofed, metasearchAdvertised, metasearchPriceId, metasearchRuleId, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, shouldApplyDiscount, showModal, subscriber, trackOfferPageEvent, activateGuestCheckoutExperiment, canProceedAsGuest])

  return { onBook, experiencesPassportFetching }
}

export function useBedbankSingleRoomAddToUserCartCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  rateListId,
  roomPosition,
  roomListId,
  flightPrice,
  connectRooms,
}: SingleRoomArgs) {
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()
  const {
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)

  const onAddToUserCart = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    isInsuranceSelected?: boolean,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      if (experiencesPassportFetching) {
        showSnackbar(intl.formatMessage({
          id: 'UCznyyUL7pToKg3_BrQl',
          defaultMessage: 'Please wait...',
          description: 'Processing modal message while bedbank booking is being processed',
        }), 'neutral')
        return
      }

      trackOfferPageEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      const pkg = packageView.package

      const accommodationItems = generateBedbankAccommodationCheckoutItems(
        offer,
        pkg,
        rate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: moment(checkOutDate).diff(moment(checkInDate), 'days'),
        },
        rooms,
      )
      const flightItems: Array<App.Checkout.FlightItem> = []
      if (withFlights && originAirport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: originAirport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: flightPrice,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }

      const insuranceItems: Array<App.Checkout.InsuranceItem> = []
      if (isInsuranceSelected) {
        const bedbankRoomIds = accommodationItems.map(item => item.roomId)
        const insuranceItem = generateInsuranceItem({ product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' }, customCoverage: rate.totals.total, bedbankRoomIds, isAccommodationBundle: true })
        insuranceItems.push(insuranceItem)
        trackClientEvent({
          subject: 'flex_cfmr_package',
          action: 'add_to_cart',
          type: 'interaction',
          category: 'insurance',
        })
      }

      fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.value, sumUpOccupancies(rooms), 'hotel'))

      const checkoutItems = [...accommodationItems, ...flightItems, ...insuranceItems]
      if (flightItems.length) {
        let closeProcessingModal: any
        showModal((resolve) => {
          closeProcessingModal = resolve
          return <ProcessingModal
            message={intl.formatMessage({
              id: 'UCznyyUL7pToKg3_BrQl',
              defaultMessage: 'Please wait...',
              description: 'Processing modal message while bedbank booking is being processed',
            })}
            fullHeight
          />
        })

        // Initiate a session
        try {
          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.roomId,
            roomRateId: rate.id,
            token: rate.token,
            rateGroupId: rate.groupId,
            isFlightBundle: rate.isFlightBundle,
            csBooking: !!isSpoofed,
            region: currentRegionCode,
            rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            price: rate.totals.inclusive,
            propertyFees: rate.totals.propertyFees,
            memberInclusive: rate.totals.memberInclusive,
            subscriber,
            source,
          })
          closeProcessingModal()

          if (!isValidSession(session)) {
            const changedRates = [{
              rate,
              newPrice: session.price,
              newLuxPlusPrice: session.luxplusPrice,
              newPropertyFees: session.propertyFees,
              rooms,
            }]
            const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
              {...calculatePriceChange(changedRates, !!subscriber?.tier)}
            />)
            if (hasUserAcceptedNewRates) {
              const newRates = updateChangedRates(dispatch, {
                offerId: offer.id,
                checkIn: checkInDate,
                checkOut: checkOutDate,
                changedRates,
              })
              onAddToUserCart(newRates[0], ratePosition, withFlights)
              return
            }
            // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
            refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
          }

          if (isValidSession(session)) {
            // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
            dispatch(initialiseCheckout([
              ...accommodationItems.map(item => ({ ...item, sessionId: session.sessionId })),
              ...flightItems,
              ...insuranceItems,
            ], { mode: 'vertical-select' }))
            dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
            dispatch(navigateToCheckout({ skipSpoofedModal: true }))
          }
        } catch(_error) {
          closeProcessingModal()
          refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
          showModal(<BedbankSoldOutModal />)
        }
      } else {
        const bedbankContextData = {
          ratePosition,
          rateListId,
          roomPosition,
          roomListId,
          rate,
          isFlightBundle: !!withFlights,
        }
        trackEvent(addToCartMultiItem(checkoutItems, { isMultiCart: true, bedbankContextData }))
        dispatch(addToUserCart(
          checkoutItems,
          false, // skipNotification
          connectRooms,
        ))
      }
    }
  }, [isSpoofed, source, subscriber, checkInDate, checkOutDate, connectRooms, currency, currentRegionCode, dispatch, experiencesPassportFetching, flightPrice, intl, isLoggedIn, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, showModal, trackOfferPageEvent])

  return onAddToUserCart
}

export interface RoomWithData {
  room: OfferPageRoom
  package: App.BedbankPackage
  rate: App.BedbankRate
  bundleRate: App.BedbankRate
  /** Either the normal or bundle rate, depending on whether flights are selected */
  effectiveRate: App.BedbankRate
  promotion?: App.BedbankPromotion
  capacityValid: boolean
}

interface MultiRoomArgs {
  offer: App.BedbankOffer
  roomsWithData: Array<RoomWithData> | undefined
  checkInDate?: string
  checkOutDate?: string
  originAirport?: App.Airport
  isLoggedIn?: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  flightPrice?: number
  connectRooms: boolean
  withFlights: boolean
}

export function useBedbankMultiRoomBookNowCallback({
  offer,
  roomsWithData: initialRoomsWithData,
  checkInDate,
  checkOutDate,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  flightPrice,
  connectRooms,
  withFlights,
}: MultiRoomArgs) {
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()
  const {
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()
  const showModal = useContext(ModalContext)

  const openDateSelector = useBedbankSelectDatesWithFlights(initialRoomsWithData?.[0]?.package.roomId)
  const onBook = useCallback(async(
    keepBrowsing = false,
    isInsuranceSelected: boolean = false,
    roomsWithData: Array<RoomWithData> | undefined = initialRoomsWithData,
  ) => {
    if (!roomsWithData) {
      return
    }
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      if (experiencesPassportFetching) {
        showSnackbar(intl.formatMessage({
          id: 'UCznyyUL7pToKg3_BrQl',
          defaultMessage: 'Please wait...',
          description: 'Processing modal message while bedbank booking is being processed',
        }), 'neutral')
        return
      }

      trackOfferPageEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal
          message={intl.formatMessage({
            id: 'UCznyyUL7pToKg3_BrQl',
            defaultMessage: 'Please wait...',
            description: 'Processing modal message while bedbank booking is being processed',
          })}
          fullHeight
        />
      })

      try {
        const roomSessions = await Promise.all(roomsWithData.map(async(roomData) => {
          const rate = roomData.effectiveRate

          // Initiate a session
          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.roomId,
            roomRateId: rate.id,
            token: rate.token,
            rateGroupId: rate.groupId,
            bedGroupId: rate.bedGroups[0].id,
            isFlightBundle: rate.isFlightBundle,
            csBooking: !!isSpoofed,
            region: currentRegionCode,
            rooms: [roomData.room],
            checkIn: checkInDate,
            checkOut: checkOutDate,
            price: rate.totals.inclusive,
            propertyFees: rate.totals.propertyFees,
            memberInclusive: rate.totals.memberInclusive,
            subscriber,
            source,
          })

          return {
            roomData,
            session,
          }
        }))

        closeProcessingModal()

        if (roomSessions.some(rs => !('sessionId' in rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE

          roomSessions
            .filter(rs => !isValidSession(rs.session))
            .forEach(roomSession => {
              const session = roomSession.session
              invariant(!isValidSession(session))
              const rate = roomSession.roomData.effectiveRate
              const room = roomSession.roomData.room
              dispatch(updateBedbankPricingData(
                offer.id,
                rate.roomId,
                rate.id,
                rate.isFlightBundle,
                checkInDate,
                checkOutDate,
                [room],
                {
                  data: [{
                    inclusive: session.price,
                    memberInclusive: session.luxplusPrice,
                    propertyFees: session.propertyFees,
                  }],
                },
              ))
            })

          const changedRates = roomSessions.map(rs => ({
            rate: rs.roomData.effectiveRate,
            newPrice: rs.session.price,
            newLuxPlusPrice: rs.session.luxplusPrice,
            newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
            rooms: [rs.roomData.room],
          }))
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            const newRoomsWithData = roomsWithData.map<RoomWithData>((rd, i) => (
              withFlights ?
                  { ...rd, bundleRate: newRates[i], effectiveRate: newRates[i] } :
                  { ...rd, rate: newRates[i], effectiveRate: newRates[i] }
            ))
            onBook(keepBrowsing, false, newRoomsWithData)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
        } else {
          // ALL GOOD, LET'S DO THIS!
          const rooms = roomsWithData.map(rd => rd.room)

          const accommodationItems = roomSessions.map(rs => {
            const session = rs.session
            invariant(isValidSession(session)) // We already checked that all sessions are valid and therefore have `sessionId`s
            return (
              generateBedbankAccommodationCheckoutItems(
                offer,
                rs.roomData.package,
                rs.roomData.effectiveRate,
                {
                  checkIn: checkInDate,
                  checkOut: checkOutDate,
                  duration: moment(checkOutDate).diff(moment(checkInDate), 'days'),
                },
                [rs.roomData.room],
                session.sessionId,
              )
            )
          }).flat()

          const flightItems: Array<App.Checkout.FlightItem> = []
          if (withFlights && originAirport) {
            const flightOccupants = countOccupantsForFlights(rooms)

            flightItems.push(generateFlightCheckoutItem({
              originAirportCode: originAirport?.code,
              destinationAirportCode: offer.flights.airportCode!,
              fareType: 'return',
              viewType: FlightViewTypes.TWO_ONE_WAYS,
              occupants: flightOccupants,
              passengers: generatePassengersFromOccupants(flightOccupants),
              quotedFare: flightPrice,
              bundledItemIds: accommodationItems.map(item => item.itemId),
            }))
          }

          const insuranceItems: Array<App.Checkout.InsuranceItem> = []
          if (isInsuranceSelected) {
            const bedbankRoomIds = accommodationItems.map(item => item.roomId)
            const totalCoverage = sum(roomsWithData, room => room.effectiveRate.totals.total)
            const insuranceItem = generateInsuranceItem({
              product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' },
              customCoverage: totalCoverage,
              bedbankRoomIds,
              isAccommodationBundle: true,
            })
            insuranceItems.push(insuranceItem)
            trackClientEvent({
              subject: 'flex_cfmr_package',
              action: 'add_to_cart',
              type: 'interaction',
              category: 'insurance',
            })
          }

          dispatch(addToCheckoutCart([
            ...accommodationItems,
            ...flightItems,
            ...insuranceItems,
          ]))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
          fireEcommerceGTMEvent(addToCartGTM(offer, currency, sum(roomSessions, rs => rs.session.price ?? 0), sumUpOccupancies(rooms), 'hotel'))

          if (keepBrowsing) {
            showSnackbar(intl.formatMessage({
              id: 'nY4_exwST6mDs_OWpjKs',
              defaultMessage: 'Your item has been added to the cart.',
              description: 'Snackbar message after adding a bedbank item to the cart',
            }), 'success')
            return
          }

          dispatch(navigateToCheckout())
        }
      } catch(_error) {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
        showModal(<BedbankSoldOutModal />)
      }
    }
  }, [checkInDate, checkOutDate, connectRooms, currency, currentRegionCode, dispatch, experiencesPassportFetching, flightPrice, initialRoomsWithData, intl, isLoggedIn, isSpoofed, offer, openDateSelector, originAirport, showModal, source, subscriber, trackOfferPageEvent, withFlights])

  return { onBook, experiencesPassportFetching }
}

export function useBedbankMultiRoomAddToUserCartCallback({
  offer,
  roomsWithData: initialRoomsWithData,
  checkInDate,
  checkOutDate,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  flightPrice,
  connectRooms,
  withFlights,
}: MultiRoomArgs) {
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()
  const {
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()

  const openDateSelector = useBedbankSelectDatesWithFlights(initialRoomsWithData?.[0]?.package.roomId)

  const onAddToUserCart = useCallback(async(
    roomsWithData: Array<RoomWithData> | undefined = initialRoomsWithData,
    isInsuranceSelected: boolean = false,
  ) => {
    if (!roomsWithData) { return undefined }
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      if (experiencesPassportFetching) {
        showSnackbar(intl.formatMessage({
          id: 'UCznyyUL7pToKg3_BrQl',
          defaultMessage: 'Please wait...',
          description: 'Processing modal message while bedbank booking is being processed',
        }), 'neutral')
        return
      }

      trackOfferPageEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }
      const rooms = roomsWithData.map(rd => rd.room)

      const accommodationItems = roomsWithData.map(rd => generateBedbankAccommodationCheckoutItems(
        offer,
        rd.package,
        rd.effectiveRate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: moment(checkOutDate).diff(moment(checkInDate), 'days'),
        },
        [rd.room],
      )).flat()

      const flightItems: Array<App.Checkout.FlightItem> = []
      if (withFlights && originAirport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: originAirport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: flightPrice,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }

      const insuranceItems: Array<App.Checkout.InsuranceItem> = []
      if (isInsuranceSelected) {
        const bedbankRoomIds = accommodationItems.map(item => item.roomId)
        const totalCoverage = sum(roomsWithData, room => room.effectiveRate.totals.total)
        const insuranceItem = generateInsuranceItem({
          product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' },
          customCoverage: totalCoverage,
          bedbankRoomIds,
          isAccommodationBundle: true,
        })
        insuranceItems.push(insuranceItem)
        trackClientEvent({
          subject: 'flex_cfmr_package',
          action: 'add_to_cart',
          type: 'interaction',
          category: 'insurance',
        })
      }

      fireEcommerceGTMEvent(addToCartGTM(offer, currency, sum(roomsWithData, rd => rd.effectiveRate.value), sumUpOccupancies(rooms), 'hotel'))
      const checkoutItems = [...accommodationItems, ...flightItems, ...insuranceItems]
      if (flightItems.length) {
        let closeProcessingModal: any
        showModal((resolve) => {
          closeProcessingModal = resolve
          return <ProcessingModal
            message={intl.formatMessage({
              id: 'UCznyyUL7pToKg3_BrQl',
              defaultMessage: 'Please wait...',
              description: 'Processing modal message while bedbank booking is being processed',
            })}
            fullHeight
          />
        })
        try {
          // Initiate sessions
          const roomSessions = await Promise.all(roomsWithData.map(async(roomData) => {
            const rate = roomData.effectiveRate

            const session = await initBedbankSession(dispatch, {
              propertyId: offer.id,
              roomTypeId: rate.roomId,
              roomRateId: rate.id,
              token: rate.token,
              rateGroupId: rate.groupId,
              isFlightBundle: rate.isFlightBundle,
              csBooking: !!isSpoofed,
              region: currentRegionCode,
              rooms: [roomData.room],
              checkIn: checkInDate,
              checkOut: checkOutDate,
              price: rate.totals.inclusive,
              propertyFees: rate.totals.propertyFees,
              memberInclusive: rate.totals.memberInclusive,
              subscriber,
              source,
            })

            return {
              roomData,
              session,
            }
          }))

          closeProcessingModal()

          if (roomSessions.some(rs => !('sessionId' in rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE

            roomSessions
              .filter(rs => !isValidSession(rs.session))
              .forEach(rs => {
                const session = rs.session
                invariant(!isValidSession(session))
                const rate = rs.roomData.effectiveRate
                const room = rs.roomData.room
                dispatch(updateBedbankPricingData(
                  offer.id,
                  rate.roomId,
                  rate.id,
                  rate.isFlightBundle,
                  checkInDate,
                  checkOutDate,
                  [room],
                  {
                    data: [{
                      inclusive: session.price,
                      memberInclusive: session.luxplusPrice,
                      propertyFees: session.propertyFees,
                    }],
                  },
                ))
              })

            const changedRates = roomSessions.map(rs => ({
              rate: rs.roomData.effectiveRate,
              newPrice: rs.session.price,
              newLuxPlusPrice: rs.session.luxplusPrice,
              newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
              rooms: [rs.roomData.room],
            }))
            const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
              {...calculatePriceChange(changedRates, !!subscriber?.tier)}
            />)
            if (hasUserAcceptedNewRates) {
              const newRates = updateChangedRates(dispatch, {
                offerId: offer.id,
                checkIn: checkInDate,
                checkOut: checkOutDate,
                changedRates,
              })
              // try book again with the updated rate
              const newRoomsWithData = roomsWithData.map<RoomWithData>((rd, i) => (
                withFlights ?
                    { ...rd, bundleRate: newRates[i], effectiveRate: newRates[i] } :
                    { ...rd, rate: newRates[i], effectiveRate: newRates[i] }
              ))
              onAddToUserCart(newRoomsWithData)
              return
            }
            // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
            refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
          } else {
          // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
            dispatch(initialiseCheckout([
              ...accommodationItems.map((item, i) => {
                const session = roomSessions[i].session
                invariant(isValidSession(session))
                return ({ ...item, sessionId: session.sessionId })
              }),
              ...flightItems,
              ...insuranceItems,
            ], { mode: 'vertical-select' }))
            dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
            dispatch(navigateToCheckout({ skipSpoofedModal: true }))
          }
        } catch(_error) {
          closeProcessingModal()
          refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
          showModal(<BedbankSoldOutModal />)
        }
      } else {
        dispatch(addToUserCart(
          checkoutItems,
          false, // skipNotification
          connectRooms,
        ))
      }
    }
  }, [initialRoomsWithData, checkInDate, checkOutDate, connectRooms, openDateSelector, trackOfferPageEvent, isLoggedIn, offer, originAirport, currency, showModal, flightPrice, dispatch, intl, isSpoofed, currentRegionCode, subscriber, source, withFlights, experiencesPassportFetching])

  return onAddToUserCart
}
